import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Switch — an on/off setting that applies the moment it moves (ADR-0088 D2).
 * There is no form to submit afterwards; a value submitted later is a
 * `Checkbox`.
 *
 * Built on Base UI (ADR-0088 D3), replacing Admin's Radix switch and Web's
 * hand-rolled `role="switch"` button. Base UI supplies `role="switch"`,
 * `aria-checked` tracked from state, Space and Enter both toggling, and the
 * next value handed to `onCheckedChange`, so no call site computes
 * `!current`.
 *
 * Two independent axes, how big and how loud:
 *
 * - **Size.** `default` (20 × 36 px) sits inline beside `text-body` label text
 *   and matches its line height, so the control never reads as the primary
 *   thing on the row. `lg` (28 × 48 px) is for a switch that owns a whole
 *   settings section.
 * - **Tone, how much the "on" state claims.** `accent` for a real setting
 *   that owns its section: the brand yellow, with an ink thumb (11.6:1, where a
 *   white thumb would be 1.5:1, under the 3:1 floor for a non-text control).
 *   `subtle` (the default) for a filter: the yellow softened to 70%, the same
 *   ink thumb. `neutral` (`control-on`) for a cosmetic
 *   preference such as a list's full-width layout, which in accent would
 *   carry the weight of the page's primary action.
 *
 * The off state is `border-strong` in every tone. As with `Checkbox`, an
 * `id` makes the control a `<button role="switch">` for a sibling
 * `<label htmlFor={id}>`; without one it is Base UI's `<span>`, named by a
 * wrapping label or an `aria-label`. Do not give an `id` to a switch inside
 * its label.
 */
import { Switch as SwitchPrimitive } from '@base-ui/react/switch';
import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';
import { cn } from './cn.js';
import { controlFocus } from './field-shape.js';
const switchVariants = cva('peer inline-flex shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors ' +
    'data-unchecked:bg-border-strong ' +
    'data-disabled:cursor-not-allowed data-disabled:opacity-50 ' +
    controlFocus, {
    variants: {
        size: {
            default: 'h-5 w-9',
            lg: 'h-7 w-12',
        },
        tone: {
            /** A setting that owns its section. */
            accent: 'data-checked:bg-accent data-checked:data-disabled:bg-accent/40 data-checked:data-disabled:opacity-100',
            /** A filter: changes which rows you see. Accent family, softened. */
            subtle: 'data-checked:bg-accent/70 data-checked:data-disabled:bg-accent/40 data-checked:data-disabled:opacity-100',
            /** A cosmetic preference. Out of the accent family entirely. */
            neutral: 'data-checked:bg-control-on',
        },
    },
    defaultVariants: { size: 'default', tone: 'subtle' },
});
const thumbVariants = cva('pointer-events-none block rounded-full bg-white shadow transition-transform data-unchecked:translate-x-0', {
    variants: {
        size: {
            // Track inner width minus thumb width, so "on" sits flush right.
            default: 'size-4 data-checked:translate-x-4',
            lg: 'size-5 data-checked:translate-x-6',
        },
        tone: {
            // On the yellow track the thumb is ink, as text on a yellow fill is;
            // disabled, the track fades and the thumb turns ochre, as a disabled
            // primary button's label does.
            accent: 'data-checked:bg-on-accent data-checked:data-disabled:bg-accent-deep',
            subtle: 'data-checked:bg-on-accent data-checked:data-disabled:bg-accent-deep',
            neutral: '',
        },
    },
    defaultVariants: { size: 'default', tone: 'subtle' },
});
export const Switch = forwardRef(({ className, size, tone, id, ...props }, ref) => (_jsx(SwitchPrimitive.Root, { ref: ref, id: id, "data-slot": "switch", nativeButton: id !== undefined, render: id !== undefined ? _jsx("button", { type: "button" }) : undefined, className: cn(switchVariants({ size, tone }), className), ...props, children: _jsx(SwitchPrimitive.Thumb, { className: thumbVariants({ size, tone }) }) })));
Switch.displayName = 'Switch';
//# sourceMappingURL=switch.js.map